import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { EmptyState } from "@/components/states/EmptyState";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Brain, RefreshCw } from "lucide-react";

type Period = "24h" | "7d" | "30d";

const PERIODS: Record<Period, { label: string; hours: number; bucket: "hour" | "day" }> = {
  "24h": { label: "Últimas 24h", hours: 24, bucket: "hour" },
  "7d": { label: "Últimos 7 dias", hours: 24 * 7, bucket: "day" },
  "30d": { label: "Últimos 30 dias", hours: 24 * 30, bucket: "day" },
};

interface EpisodeRow {
  id: string;
  org_id: string;
  group_id: string;
  created_at: string;
  consolidated_at: string | null;
  episode_started_at: string;
  episode_ended_at: string;
  salience: number;
  participants: string[] | null;
  message_ids: string[] | null;
}

export default function LtmDashboard() {
  const [period, setPeriod] = useState<Period>("7d");
  const cfg = PERIODS[period];

  const sinceIso = useMemo(
    () => new Date(Date.now() - cfg.hours * 3600 * 1000).toISOString(),
    [cfg.hours],
  );

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["ltm-episodes", period],
    queryFn: async (): Promise<EpisodeRow[]> => {
      const { data, error } = await supabase
        .from("syra_episodic_memories")
        .select(
          "id, org_id, group_id, created_at, consolidated_at, episode_started_at, episode_ended_at, salience, participants, message_ids",
        )
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as EpisodeRow[];
    },
  });

  const { data: totalAll } = useQuery({
    queryKey: ["ltm-episodes-total"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("syra_episodic_memories")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
  });

  const series = useMemo(() => {
    if (!data) return [];
    const buckets = new Map<string, number>();
    const fmt = (d: Date) => {
      if (cfg.bucket === "hour") {
        const x = new Date(d);
        x.setMinutes(0, 0, 0);
        return x.toISOString();
      }
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x.toISOString();
    };
    // pre-fill empty buckets
    const stepMs = cfg.bucket === "hour" ? 3600 * 1000 : 86400 * 1000;
    const start = new Date(Date.now() - cfg.hours * 3600 * 1000);
    const end = new Date();
    for (let t = fmt(start); new Date(t).getTime() <= end.getTime(); ) {
      buckets.set(t, 0);
      const next = new Date(new Date(t).getTime() + stepMs);
      t = fmt(next);
      if (buckets.has(t)) break;
    }
    for (const row of data) {
      const k = fmt(new Date(row.created_at));
      buckets.set(k, (buckets.get(k) ?? 0) + 1);
    }
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, count]) => ({
        ts,
        label:
          cfg.bucket === "hour"
            ? new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
            : new Date(ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        count,
      }));
  }, [data, cfg]);

  const byOrg = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { org_id: string; episodes: number; groups: Set<string>; lastAt: string; salienceSum: number }>();
    for (const row of data) {
      const cur =
        map.get(row.org_id) ??
        { org_id: row.org_id, episodes: 0, groups: new Set<string>(), lastAt: row.created_at, salienceSum: 0 };
      cur.episodes += 1;
      cur.groups.add(row.group_id);
      cur.salienceSum += Number(row.salience ?? 0);
      if (row.created_at > cur.lastAt) cur.lastAt = row.created_at;
      map.set(row.org_id, cur);
    }
    return Array.from(map.values())
      .map((r) => ({
        org_id: r.org_id,
        episodes: r.episodes,
        groups: r.groups.size,
        avgSalience: r.episodes ? r.salienceSum / r.episodes : 0,
        lastAt: r.lastAt,
      }))
      .sort((a, b) => b.episodes - a.episodes);
  }, [data]);

  const byGroup = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { group_id: string; org_id: string; episodes: number; lastAt: string; participants: Set<string>; salienceSum: number }>();
    for (const row of data) {
      const cur =
        map.get(row.group_id) ??
        {
          group_id: row.group_id,
          org_id: row.org_id,
          episodes: 0,
          lastAt: row.created_at,
          participants: new Set<string>(),
          salienceSum: 0,
        };
      cur.episodes += 1;
      cur.salienceSum += Number(row.salience ?? 0);
      for (const p of row.participants ?? []) cur.participants.add(p);
      if (row.created_at > cur.lastAt) cur.lastAt = row.created_at;
      map.set(row.group_id, cur);
    }
    return Array.from(map.values())
      .map((r) => ({
        group_id: r.group_id,
        org_id: r.org_id,
        episodes: r.episodes,
        participants: r.participants.size,
        avgSalience: r.episodes ? r.salienceSum / r.episodes : 0,
        lastAt: r.lastAt,
      }))
      .sort((a, b) => b.episodes - a.episodes);
  }, [data]);

  const totalPeriod = data?.length ?? 0;
  const uniqueOrgs = byOrg.length;
  const uniqueGroups = byGroup.length;
  const avgSalience =
    totalPeriod > 0
      ? data!.reduce((s, r) => s + Number(r.salience ?? 0), 0) / totalPeriod
      : 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold">LTM — Memórias Episódicas</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Acompanhe a geração de <code>syra_episodic_memories</code> por período, org e grupo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <TabsList>
              {(Object.keys(PERIODS) as Period[]).map((p) => (
                <TabsTrigger key={p} value={p}>
                  {PERIODS[p].label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <KpiCard label="No período" value={totalPeriod.toLocaleString("pt-BR")} hint={cfg.label} />
        <KpiCard label="Total acumulado" value={(totalAll ?? 0).toLocaleString("pt-BR")} hint="desde o início" />
        <KpiCard label="Orgs ativas" value={uniqueOrgs.toLocaleString("pt-BR")} hint="com episódios no período" />
        <KpiCard label="Salience média" value={avgSalience.toFixed(2)} hint={`${uniqueGroups} grupos`} />
      </div>

      {/* Timeseries */}
      <Card>
        <CardHeader>
          <CardTitle>Evolução temporal</CardTitle>
          <CardDescription>
            Episódios consolidados por {cfg.bucket === "hour" ? "hora" : "dia"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSkeleton count={6} />
          ) : series.length === 0 ? (
            <EmptyState title="Sem dados" description="Nenhum episódio consolidado neste período." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="ltmFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      color: "hsl(var(--popover-foreground))",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#ltmFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown */}
      <Tabs defaultValue="org" className="space-y-4">
        <TabsList>
          <TabsTrigger value="org">Por organização</TabsTrigger>
          <TabsTrigger value="group">Por grupo</TabsTrigger>
        </TabsList>

        <TabsContent value="org">
          <Card>
            <CardHeader>
              <CardTitle>Top organizações</CardTitle>
              <CardDescription>Episódios consolidados no período</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton count={6} />
              ) : byOrg.length === 0 ? (
                <EmptyState title="Sem dados" description="Nenhuma org com episódios neste período." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Org</TableHead>
                      <TableHead className="text-right">Episódios</TableHead>
                      <TableHead className="text-right">Grupos</TableHead>
                      <TableHead className="text-right">Salience média</TableHead>
                      <TableHead className="text-right">Último</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byOrg.slice(0, 20).map((r) => (
                      <TableRow key={r.org_id}>
                        <TableCell className="font-mono text-xs">{r.org_id.slice(0, 8)}…</TableCell>
                        <TableCell className="text-right font-medium">{r.episodes}</TableCell>
                        <TableCell className="text-right">{r.groups}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{r.avgSalience.toFixed(2)}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {new Date(r.lastAt).toLocaleString("pt-BR")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="group">
          <Card>
            <CardHeader>
              <CardTitle>Top grupos</CardTitle>
              <CardDescription>Episódios consolidados no período</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton count={6} />
              ) : byGroup.length === 0 ? (
                <EmptyState title="Sem dados" description="Nenhum grupo com episódios neste período." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Org</TableHead>
                      <TableHead className="text-right">Episódios</TableHead>
                      <TableHead className="text-right">Participantes</TableHead>
                      <TableHead className="text-right">Salience média</TableHead>
                      <TableHead className="text-right">Último</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byGroup.slice(0, 30).map((r) => (
                      <TableRow key={r.group_id}>
                        <TableCell className="font-mono text-xs">{r.group_id.slice(0, 8)}…</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {r.org_id.slice(0, 8)}…
                        </TableCell>
                        <TableCell className="text-right font-medium">{r.episodes}</TableCell>
                        <TableCell className="text-right">{r.participants}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{r.avgSalience.toFixed(2)}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {new Date(r.lastAt).toLocaleString("pt-BR")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
      {hint && (
        <CardContent>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </CardContent>
      )}
    </Card>
  );
}
